import DiscountIcon from 'assets/svg/discount/discount.svg'
import FreeShippingIcon from 'assets/svg/free-shipping/free-shipping.svg'
import PinIcon from 'assets/svg/pin/pin.svg'
import Badge from 'common/components/Badge'
import Divider from 'common/components/Divider'
import Flex from 'common/components/Flex'
import Image from 'common/components/Image'
import Rating from 'common/components/Rating'
import IconStar from 'common/components/Rating/IconStar'
import Typography from 'common/components/Typography'
import theme from 'common/theme'
import cdnUrl from 'config/cdn'
import cdn, { mediaflexCdn } from 'lib/cdn'
import CurrencyFormatter from 'lib/currency-formatter'
import styled from 'styled-components'

const Container = styled.div`
  background-color: ${theme.colors.white};
  border-radius: 10px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.11);
  height: ${({ height, type }) =>
    height || type === 'direct' ? '280px' : '323px'};
  width: ${({ width }) => width || '180px'};
`

const ImageContainer = styled.div`
  position: relative;
  width: ${({ width }) => width || '168px'};
  height: ${({ imageHeight }) => imageHeight || '160px'};
  padding: 6px 6px 2px 6px;
`

const FreeShippingIconWrapper = styled.span`
  position: absolute;
  bottom: 0;
  left: 15px;
  visibility: ${({ isFreeShipping }) =>
    isFreeShipping ? 'display' : 'hidden'};
`

const DividerContainer = styled.div`
  margin-top: 1px;
`

const ProductDetailContainer = styled.div`
  width: 160px;
  margin: 5px 10px 5px 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  height: 36px;
  color: ${theme.colors.black};
`

const UnitAndRatingContainer = styled.div`
  width: 160px;
  margin: 0 10px 6px 9px;
  display: flex;
  justify-content: space-between;
`
const UnitWrapper = styled.span``

const RatingWrapper = styled.span`
  color: ${theme.colors.black};

  > * {
    color: ${theme.colors.orangeYellow};
  }
`

const CurrentPriceContainer = styled.div`
  width: 160px;
  height: 22px;
  margin: ${({ isDiscount }) =>
    isDiscount ? '0px 10px -3px 10px;' : '8px 10px 8px 10px;'};
`

const PreviousPriceContainer = styled.div`
  margin: 0px 0px 0px 10px;
  align-items: center;
  height: 14px;
  display: ${({ previousPrice, currentPrice }) =>
    currentPrice && previousPrice !== currentPrice
      ? 'inline-flex'
      : 'none'};
`

const PreviousPriceWrapper = styled.span`
  text-decoration: line-through;
  text-decoration-color: gray;
  margin-right: 5px;
`

const DiscountContainer = styled.div`
  visibility: ${({ discount }) => (discount ? 'display' : 'hidden')};
`

const LocationContainer = styled.div`
  margin: ${({ type }) =>
    type === 'direct' ? '0px 9px 0px 9px' : '4px 9px 0px 9px;'};
  display: inline-flex;
  align-items: center;
  height: 14px;
  visibility: ${({ location }) => (location ? 'display' : 'hidden')};
`

const PinIconWrapper = styled.span``

const LocationNameWrapper = styled.span`
  height: 14px;
  color: ${theme.colors.black};
  opacity: 0.5;
  font-size: 10px;
  margin-left: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 120px;
  white-space: nowrap;
`
const StoreAndSellerMembershipContainer = styled.div`
  width: 160px;
  height: 14px;
  display: flex;
  justify-content: space-between;
  margin: 2px 4px 12px 10px;
  bottom: 5px;
  align-items: baseline;
  position: relative;
`

const StoreNameWrapper = styled.span`
  color: ${theme.colors.black};
  opacity: 0.5;
  font-size: 10px;
  overflow: hidden;
  width: 120px;
  height: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: ${({ type, previousPrice, currentPrice }) =>
    type === 'agent' &&
    currentPrice &&
    previousPrice !== currentPrice &&
    '10px'};
`

const SellerMembershipBadgeWrapper = styled.span`
  visibility: ${({ sellerMembershipBadge }) =>
    sellerMembershipBadge ? 'display' : 'hidden'};
  display: flex;
  position: absolute;
  right: 1.8px;
  bottom: 2.5px;
`

interface IProductCard {
  type?: 'default' | 'agent' | 'direct' | 'recomendation'
  height?: string
  width?: string
  image: string
  imageHeight?: string
  name: string
  currentPrice: string
  previousPrice: string
  unit?: string
  rating: number
  location?: string
  storeName: string
  sellerMembershipBadge?: string
  isFreeShipping?: boolean
  productURL: string
  onClickProduct?: any
  percentage?: string
  maskedPrice?: string
  maskedDiscount?: string
  isShowRating?: boolean
  potentialProfit?: string
}

function ProductCard({
  type = 'default',
  height,
  width,
  image,
  imageHeight,
  name,
  unit,
  rating,
  currentPrice,
  previousPrice,
  location,
  storeName,
  sellerMembershipBadge,
  isFreeShipping,
  productURL,
  onClickProduct,
  percentage,
  maskedDiscount,
  maskedPrice,
  isShowRating,
  potentialProfit,
}: IProductCard): JSX.Element {
  const checkImage = (imagePath) => {
    if (imagePath.includes(cdnUrl.baseCDNUrl)) {
      return imagePath
    }
    return mediaflexCdn(`f=auto,w=160/${image}`)
  }

  return (
    <a href={productURL} target="#" onClick={onClickProduct}>
      <Container
        height={height}
        width={width}
        data-testid="product-card-component"
        type={type}
      >
        <ImageContainer>
          <Image
            src={checkImage(image)}
            height={imageHeight || '160px'}
            borderTopLeftRadius="6px"
            borderTopRightRadius="6px"
            objectFit="contain"
            lazy
          />
          <FreeShippingIconWrapper isFreeShipping={isFreeShipping}>
            <FreeShippingIcon />
          </FreeShippingIconWrapper>
        </ImageContainer>
        <DividerContainer>
          <Divider
            color={theme.colors.grayLight}
            width="100%"
            height="1px"
            borderRadius="0px"
          />
        </DividerContainer>
        {type === 'agent' && (
          <Flex
            margin="5px 10px 5px 10px"
            gap="5px"
            alignItems="center"
          >
            <DiscountIcon />
            <Flex flexDirection="column">
              <Typography
                fontSize={theme.typography.rlead.fontSize}
                color="gray"
              >
                Potensi Keuntungan
              </Typography>
              <Typography color="orange" fontWeight="bold">
                {CurrencyFormatter(parseFloat(potentialProfit))}
              </Typography>
            </Flex>
          </Flex>
        )}
        {type === 'recomendation' && (
          <Flex justifyContent="space-between" margin="5px 10px">
            {isShowRating && (
              <RatingWrapper>
                <IconStar />
                {rating}
              </RatingWrapper>
            )}
            <UnitWrapper>
              <Badge
                content={unit}
                backgroundColor={theme.colors.grayLighter}
                fontColor={theme.colors.black}
                border={`solid 1px ${theme.colors.grayLighter}`}
                borderRadius="7.5px"
                position="left"
                height="15px"
                fontSize="9px"
              />
            </UnitWrapper>
          </Flex>
        )}
        <ProductDetailContainer>
          <Typography fontSize="13px" color={theme.colors.black}>
            {name}
          </Typography>
        </ProductDetailContainer>
        {type === 'default' && (
          <UnitAndRatingContainer>
            <UnitWrapper>
              <Badge
                content={unit}
                backgroundColor={theme.colors.white}
                fontColor={theme.colors.gray}
                border={`solid 1px ${theme.colors.gray}`}
                borderRadius="7.5px"
                position="left"
                height="15px"
                fontSize="9px"
              />
            </UnitWrapper>
            {isShowRating && (
              <RatingWrapper>
                <Rating rating={rating} />
              </RatingWrapper>
            )}
          </UnitAndRatingContainer>
        )}
        {maskedPrice && (
          <>
            <CurrentPriceContainer>
              <Typography
                fontSize="16px"
                fontWeight="bold"
                color="orange"
              >
                {maskedPrice}
              </Typography>
            </CurrentPriceContainer>
            <PreviousPriceContainer
              previousPrice={maskedPrice}
              currentPrice={maskedDiscount ? '-' : maskedPrice}
            >
              <PreviousPriceWrapper>
                <Typography fontSize="10px" color="gray">
                  {maskedDiscount}
                </Typography>
              </PreviousPriceWrapper>
              <DiscountContainer discount={percentage}>
                <Badge
                  content={percentage}
                  backgroundColor={theme.colors.redLighter}
                  fontColor={theme.colors.red}
                  borderRadius="4px"
                  position="static"
                  height="14px"
                  fontSize="8px"
                />
              </DiscountContainer>
            </PreviousPriceContainer>
          </>
        )}
        {!maskedPrice && (
          <>
            <CurrentPriceContainer
              isDiscount={previousPrice !== currentPrice}
            >
              <Typography
                fontSize="16px"
                fontWeight="bold"
                color="orange"
              >
                {currentPrice
                  ? CurrencyFormatter(parseFloat(currentPrice))
                  : CurrencyFormatter(parseFloat(previousPrice))}
              </Typography>
            </CurrentPriceContainer>
            <PreviousPriceContainer
              previousPrice={previousPrice}
              currentPrice={currentPrice}
            >
              <PreviousPriceWrapper>
                <Typography fontSize="10px" color="gray">
                  {previousPrice
                    ? CurrencyFormatter(parseFloat(previousPrice))
                    : 'Rp 0'}
                </Typography>
              </PreviousPriceWrapper>
              <DiscountContainer discount={percentage}>
                <Badge
                  content={percentage}
                  backgroundColor={theme.colors.redLighter}
                  fontColor={theme.colors.red}
                  borderRadius="4px"
                  position="static"
                  height="14px"
                  fontSize="8px"
                />
              </DiscountContainer>
            </PreviousPriceContainer>
          </>
        )}
        {type !== 'agent' && (
          <LocationContainer location={location} type={type}>
            <PinIconWrapper>
              <PinIcon />
            </PinIconWrapper>
            <LocationNameWrapper>{location}</LocationNameWrapper>
          </LocationContainer>
        )}
        {type !== 'direct' && (
          <StoreAndSellerMembershipContainer>
            <StoreNameWrapper
              type={type}
              currentPrice={currentPrice}
              previousPrice={previousPrice}
            >
              {storeName}
            </StoreNameWrapper>
            <SellerMembershipBadgeWrapper
              sellerMembershipBadge={sellerMembershipBadge}
            >
              {sellerMembershipBadge && (
                <Image
                  src={cdn({
                    path: `/${sellerMembershipBadge}`,
                  })}
                  objectFit="contain"
                  width="14px"
                  height="14px"
                  lazy
                />
              )}
            </SellerMembershipBadgeWrapper>
          </StoreAndSellerMembershipContainer>
        )}
      </Container>
    </a>
  )
}

export default ProductCard
